import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { useAuth, useRole } from '@structural-climbing/auth'
import { Card, CardBody } from '@structural-climbing/ui'
import { useGymContext } from '../../contexts/GymContext'

type ItemKey = 'branding' | 'plans' | 'sections' | 'legal' | 'team'

interface OnboardingItem {
  key: ItemKey
  done: boolean
}

interface OnboardingScore {
  score: number
  total: number
  complete: boolean
  items: OnboardingItem[]
}

// Each step links to the page that completes it. Branding and legal both live
// under Settings (separate tabs); sections are managed on the Routes page.
const ITEM_PATH: Record<ItemKey, string> = {
  branding: 'settings',
  plans: 'plans',
  sections: 'routes',
  legal: 'settings',
  team: 'team',
}

/**
 * Setup-completion card shown to the gym owner at the top of the dashboard.
 * Staff never see it: the steps lead to owner-only pages. It renders until all
 * five steps are done, then disappears for good. It stays silent while loading
 * or on error so it never flashes a half-empty card.
 */
export function GymSetupChecklist() {
  const { slug } = useGymContext()
  const { getAccessTokenSilently } = useAuth()
  const isOwner = useRole() === 'gym_owner'
  const { t } = useTranslation()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const [data, setData] = useState<OnboardingScore | null>(null)

  useEffect(() => {
    if (!isOwner) return
    let cancelled = false
    async function load() {
      try {
        const token = await getAccessTokenSilently()
        const res = await fetch(`${apiUrl}/my-gym-onboarding`, {
          headers: { Authorization: `Bearer ${token}` },
        })
        if (!res.ok) return
        const body = (await res.json()) as OnboardingScore
        if (!cancelled) setData(body)
      } catch {
        // Non-critical: the checklist simply doesn't render.
      }
    }
    void load()
    return () => {
      cancelled = true
    }
  }, [apiUrl, getAccessTokenSilently, isOwner])

  if (!isOwner || !data || !Array.isArray(data.items) || data.complete) return null

  return (
    <Card className="mb-8 border border-primary/20 bg-primary/5">
      <CardBody className="gap-3">
        <div className="flex items-center justify-between gap-2">
          <div>
            <h2 className="text-base font-semibold">{t('setup.title')}</h2>
            <p className="text-xs text-foreground/50">{t('setup.subtitle')}</p>
          </div>
          <span className="shrink-0 text-sm font-medium text-foreground/60">
            {t('setup.progress', { done: data.score, total: data.total })}
          </span>
        </div>
        <ul className="flex flex-col gap-1.5">
          {data.items.map((item) =>
            item.done ? (
              <li key={item.key} className="flex items-center gap-2 text-sm text-foreground/40">
                <CheckCircle />
                <span className="line-through">{t(`setup.item.${item.key}`)}</span>
              </li>
            ) : (
              <li key={item.key}>
                <Link
                  to={`/${slug}/${ITEM_PATH[item.key]}`}
                  className="flex items-center gap-2 text-sm text-foreground/80 transition-colors hover:text-primary"
                >
                  <EmptyCircle />
                  <span>{t(`setup.item.${item.key}`)}</span>
                  <span aria-hidden className="text-foreground/40">
                    →
                  </span>
                </Link>
              </li>
            ),
          )}
        </ul>
      </CardBody>
    </Card>
  )
}

function CheckCircle() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 20 20"
      className="h-4 w-4 shrink-0 text-success"
      fill="currentColor"
    >
      <path
        fillRule="evenodd"
        d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.7-9.3a1 1 0 00-1.4-1.4L9 10.6 7.7 9.3a1 1 0 00-1.4 1.4l2 2a1 1 0 001.4 0l4-4z"
        clipRule="evenodd"
      />
    </svg>
  )
}

function EmptyCircle() {
  return (
    <svg
      aria-hidden
      viewBox="0 0 20 20"
      className="h-4 w-4 shrink-0 text-foreground/30"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
    >
      <circle cx="10" cy="10" r="7.25" />
    </svg>
  )
}
